import styled, { css } from "styled-components";

// Shared building blocks for the redesigned pages. Colours come from the
// CSS tokens in index.css so light and dark mode stay in sync.

export const Container = styled.div`
  width: 100%;
  max-width: var(--max-w);
  margin: 0 auto;
  padding: 0 var(--gutter);
`;

export const Section = styled.section`
  padding: clamp(64px, 9vw, 112px) 0;
`;

export const Eyebrow = styled.span`
  display: inline-block;
  font-family: var(--font-display);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--coral);
  margin-bottom: 14px;
`;

export const H2 = styled.h2`
  font-size: clamp(30px, 4.2vw, 46px);
  line-height: 1.1;
  letter-spacing: -0.025em;
  margin-bottom: 16px;
`;

export const Lead = styled.p`
  font-size: clamp(16px, 1.4vw, 18px);
  line-height: 1.7;
  color: var(--text-muted);
  max-width: 62ch;
`;

const buttonBase = css`
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 48px;
  padding: 0 26px;
  border-radius: var(--radius-pill);
  border: 1.5px solid transparent;
  font-family: var(--font-display);
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.01em;
  white-space: nowrap;
  text-decoration: none;
  cursor: pointer;
  transition:
    transform 0.35s var(--ease-out),
    box-shadow 0.35s var(--ease-out),
    background-color 0.25s ease,
    border-color 0.25s ease,
    color 0.25s ease;

  svg {
    transition: transform 0.35s var(--ease-out);
  }

  &:hover {
    transform: translateY(-2px);
  }

  &:hover svg {
    transform: translateX(3px);
  }

  &:active {
    transform: translateY(0) scale(0.98);
  }

  &:disabled {
    opacity: 0.65;
    cursor: progress;
    transform: none;
  }
`;

export const buttonStyles = {
  primary: css`
    ${buttonBase}
    background: var(--coral);
    color: var(--on-accent);
    box-shadow: 0 10px 24px -10px rgb(247 108 108 / 0.7);

    &:hover {
      background: var(--coral-strong);
      box-shadow: 0 16px 30px -12px rgb(247 108 108 / 0.8);
    }
  `,
  ghost: css`
    ${buttonBase}
    background: transparent;
    color: var(--text);
    border-color: var(--border-strong);

    &:hover {
      border-color: var(--text);
    }
  `,
  dark: css`
    ${buttonBase}
    background: var(--navy);
    color: #fefefe;
    box-shadow: 0 14px 30px -14px rgb(36 48 94 / 0.8);

    &:hover {
      background: var(--indigo);
    }
  `,
  light: css`
    ${buttonBase}
    background: rgb(254 254 254 / 0.12);
    color: #fefefe;
    border-color: rgb(254 254 254 / 0.55);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);

    &:hover {
      background: rgb(254 254 254 / 0.22);
      border-color: #fefefe;
    }
  `,
};

export const Button = styled.button`
  ${({ $variant = "primary" }) => buttonStyles[$variant]}
  ${({ $block }) => $block && "width: 100%;"}
`;

export const ButtonLink = styled.a`
  ${({ $variant = "primary" }) => buttonStyles[$variant]}
`;

export const TextLink = styled.button`
  appearance: none;
  background: none;
  border: 0;
  padding: 0;
  font: inherit;
  font-weight: 600;
  color: var(--indigo);
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;
  text-decoration-thickness: 1.5px;

  @media (prefers-color-scheme: dark) {
    color: var(--butter);
  }

  &:hover {
    color: var(--coral);
  }
`;

export const fieldStyles = css`
  width: 100%;
  min-height: 48px;
  padding: 10px 14px;
  font-family: var(--font-body);
  font-size: 16px;
  font-weight: 500;
  line-height: 1.4;
  color: var(--text);
  background-color: var(--input-bg);
  border: 1.5px solid var(--border);
  border-radius: var(--radius-input);
  transition:
    border-color 0.2s ease,
    box-shadow 0.25s var(--ease-out);

  &::placeholder {
    color: var(--placeholder);
  }

  &:hover {
    border-color: var(--border-strong);
  }

  &:focus {
    outline: none;
    border-color: var(--indigo);
    box-shadow: 0 0 0 4px rgb(55 71 133 / 0.18);
  }

  @media (prefers-color-scheme: dark) {
    &:focus {
      border-color: var(--butter);
      box-shadow: 0 0 0 4px rgb(248 233 161 / 0.16);
    }
  }
`;

export const labelStyles = css`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: var(--text);
  margin-bottom: 8px;
`;

export const Label = styled.label`
  ${labelStyles}
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  margin: 0 0 16px;
  padding: 0;
  border: 0;

  input:not([type="radio"]):not([type="checkbox"]),
  select,
  textarea {
    ${fieldStyles}
  }
`;
